import { BoxProps } from '@chakra-ui/react';
import { ReactNode, useRef } from 'react';
import { useDrop } from 'react-dnd';
import { MBox } from '../../../../../components/Monetize';
import {
  MANAGE_GROUPS_OFFERING_DRAG_TYPE,
  ManageGroupsOfferingDragItem,
} from './manageQuoteOfferingGroupsConstants';

interface ManageQuoteOfferingGroupsDropZoneProps extends BoxProps {
  /** Target of the drop; null for the Ungrouped strip */
  rowKey: string | null;
  onAssign: (quoteOfferingId: string, toRowKey: string | null) => void;
  /** Nothing can be dropped here — the card renders as a plain solid card */
  isDropDisabled?: boolean;
  children: ReactNode;
}

/**
 * A container offerings can be dropped into — a group card or the Ungrouped
 * strip. Both look and behave identically: a dashed edge marks the container as
 * droppable, and it turns indigo while a chip is over it. No fill (a colour wash
 * behind live form controls reads as disabled) and no focus halo (that shadow is
 * sized for inputs, not cards).
 *
 * With drops disabled the dashed edge goes solid: dashes promise a drop target,
 * and on a read-only quote there isn't one.
 */
export const ManageQuoteOfferingGroupsDropZone = ({
  rowKey,
  onAssign,
  isDropDisabled = false,
  children,
  ...boxProps
}: ManageQuoteOfferingGroupsDropZoneProps) => {
  const dropRef = useRef<HTMLDivElement>(null);
  const [{ isActive }, drop] = useDrop<
    ManageGroupsOfferingDragItem,
    void,
    { isActive: boolean }
  >(
    () => ({
      accept: MANAGE_GROUPS_OFFERING_DRAG_TYPE,
      canDrop: ({ fromRowKey }) => !isDropDisabled && fromRowKey !== rowKey,
      drop: ({ quoteOfferingId }) => onAssign(quoteOfferingId, rowKey),
      collect: (monitor) => ({
        isActive: monitor.isOver() && monitor.canDrop(),
      }),
    }),
    [rowKey, onAssign, isDropDisabled],
  );
  drop(dropRef);

  return (
    <MBox
      ref={dropRef}
      bg="tWhite.base"
      borderWidth="1px"
      borderStyle={isDropDisabled ? 'solid' : 'dashed'}
      borderColor={isActive ? 'tIndigo.base' : 'border.subtle'}
      borderRadius="md"
      transition="border-color 0.15s"
      {...boxProps}
    >
      {children}
    </MBox>
  );
};
